import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Card } from '@parrot-software/design-system';
import { ChevronRight } from '@parrot-software/design-system/icons';
import type { PaymentMethodViewDto } from '@subscriptions/application/dtos';
import type { PaymentSessionMode } from '@subscriptions/domain/types';
import { displayValue, formatBrand } from '@subscriptions/presentation/utils';

import type { WizardOrigin } from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import { CREATE_ORIGIN } from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';

import { cardConfirmationStyles as styles } from './CardConfirmation.styles';
import { ConfirmationSuccessHeader } from './ConfirmationSuccessHeader';
import { ConfirmationSummaryRows } from './ConfirmationSummaryRows';

type CardSavedConfirmationProps = {
  blockUuid: string;
  origin: WizardOrigin;
  mode: PaymentSessionMode;
  paymentMethod: PaymentMethodViewDto;
};

/**
 * Terminal card confirmation (wizard step 5, `card-saved` variant — scenario
 * `NEW_PAYMENT_METHOD_AND_CHARGE`).
 *
 * Shown after a brand-new card is registered and the $10 MXN hold runs. Renders
 * the saved method (brand + `•••• last4`), its expiration, the "$10.00
 * reembolsable" copy and a monthly auto-charge note. The card fields come from the
 * `confirm-session` response carried in route state, already normalized into the
 * shared {@link PaymentMethodViewDto} (`brand` ← `providerPublicBrand`,
 * `expirationDate` ← `providerPublicMonth`/`providerPublicYear`, `providerPublicId`
 * = last-four for CARD). The plan name is intentionally never shown. The primary
 * action routes to the block detail page.
 *
 * The title switches on the wizard entry origin: "¡Sucursales agrupadas y tarjeta
 * guardada!" in the creation flow, "¡Tarjeta guardada!" when re-entering from the
 * block detail to change/configure the payment method.
 *
 * @param props.blockUuid - Subscription block uuid for the in-flight wizard
 * @param props.origin - Flow the wizard was entered through
 * @param props.mode - Stripe payment session mode that produced this confirmation
 * @param props.paymentMethod - Card payment method returned by confirm-session
 * @returns React element rendering the card-saved confirmation
 */
export const CardSavedConfirmation = ({ blockUuid, origin, mode, paymentMethod }: CardSavedConfirmationProps) => {
  const { t } = useTranslation('subscriptions');
  const history = useHistory();

  const handleGoToSubscription = () => {
    history.push(`/subscriptions/blocks/${blockUuid}`);
  };

  return (
    <div className={styles.root} data-mode={mode}>
      <ConfirmationSuccessHeader
        iconSize={60}
        layout="stacked"
        title={
          origin === CREATE_ORIGIN
            ? t('blockCreationWizard.confirmation.cardSaved.groupedTitle')
            : t('blockCreationWizard.confirmation.cardSaved.successTitle')
        }
      />

      <Card className={styles.card}>
        <ConfirmationSummaryRows
          rows={[
            {
              label: t('paymentMethod.card'),
              value: t('paymentMethod.cardLabel', {
                brand: formatBrand(paymentMethod.brand),
                lastFourDigits: displayValue(paymentMethod.providerPublicId),
              }),
            },
            {
              label: t('blockCreationWizard.confirmation.card.expirationLabel'),
              value: displayValue(paymentMethod.expirationDate),
            },
            {
              label: t('blockCreationWizard.confirmation.cardSaved.verificationChargeLabel'),
              value: t('blockCreationWizard.confirmation.cardSaved.verificationChargeValue'),
            },
          ]}
        />
      </Card>

      <div className={styles.noteBox}>
        <p className={styles.notePrimary}>{t('blockCreationWizard.confirmation.cardSaved.autoChargeNote')}</p>
        <p className={styles.noteSecondary}>{t('blockCreationWizard.confirmation.cardSaved.confirmationEmailNote')}</p>
      </div>

      <Button className={styles.primaryButton} onClick={handleGoToSubscription}>
        {t('blockCreationWizard.confirmation.primaryAction')}
        <ChevronRight aria-hidden className="h-4 w-4" />
      </Button>
    </div>
  );
};
